import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StoreState } from 'src/store/configureStore';
import { useSelector } from 'react-redux';
import useTranslate from 'src/utils/useTranslate';
import { Button, List, message, Modal, Form, Input, } from 'antd';
import { axiosInstance } from 'src/utils/sendApi';
import { AsyncStates, defaultHeaders } from 'src/constants';
import jwtManager from 'src/utils/jwtManager';
import { messages } from 'src/utils/hooks';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from "dayjs"
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type RegisterOpts = PublicKeyCredentialCreationOptionsJSON;

export default function RegisterPassKey() {
    const [t] = useTranslate();
    const currentLanguage = useSelector((state: StoreState) => state.language.current);
    const [form] = Form.useForm();

    const [status, setStatus] = useState(AsyncStates.INITIAL);
    const [error, setError] = useState<string | null>();

    const [deleteModal, setDeleteModal] = useState<{
        show: boolean,
        credential: any,
        loading: boolean
    }>({
        show: false,
        credential: null,
        loading: false
    })
    const [credentials, setCredentials] = useState<{ data: unknown[], message: string | null, status: string | null }>({
        data: [],
        message: null,
        status: null

    })

    const user_email = useSelector<StoreState, string>((state) => state.login.loginResponse.user_email);

    const SWB = (window as any).SimpleWebAuthnBrowser;

    const headers = useMemo(() => ({
        ...defaultHeaders,
        token: jwtManager.getToken() as string,
    }), [])

    const fetchCredentials = useCallback(async () => {
        setCredentials((prev) => ({
            ...prev,
            message: null,
            status: "Loading"
        }))
        try {

            const { data: { result } } = await axiosInstance.post<any>(
                process.env.REACT_APP_API_URL + 'v1/data/_passkey_info',
                { user_email },
                { headers }
            );

            setCredentials({
                data: result?.data ?? [],
                message: result?.message,
                status: result.status
            })

        } catch (error) {
            setCredentials((prev) => ({
                ...prev,
                message: messages[currentLanguage]?.internal_server_error,
                status: "Failed"
            }))
        }
    }, [headers, user_email, currentLanguage])

    useEffect(() => {
        fetchCredentials()
    }, [fetchCredentials])

    function getDeviceInfoString() {
        const ua = navigator.userAgent;

        let browser = "Browser";
        if (/Chrome\/\d+/.test(ua) && !/Edg/.test(ua) && !/OPR/.test(ua)) browser = "Chrome";
        else if (/Safari\/\d+/.test(ua) && !/Chrome/.test(ua)) browser = "Safari";
        else if (/Firefox\/\d+/.test(ua)) browser = "Firefox";
        else if (/Edg\/\d+/.test(ua)) browser = "Edge";
        else if (/OPR\/\d+/.test(ua)) browser = "Opera";
        else if (/MSIE \d+/.test(ua) || /Trident\/\d+/.test(ua)) browser = "Internet Explorer";

        let os = "Unknown OS";
        if (/Macintosh|Mac OS X/.test(ua)) os = "MacOS";
        else if (/Windows/.test(ua)) os = "Windows";
        else if (/Android/.test(ua)) os = "Android";
        else if (/iPhone|iPad/.test(ua)) os = "iOS";
        else if (/Linux/.test(ua)) os = "Linux";

        // Return formatted string
        return `${browser} on ${os}`;
    }

    const handleRegister = async (values: { display_name: string }) => {
        if (!SWB?.browserSupportsWebAuthn()) {
            setError('⚠️ WebAuthn not supported');
            return
        }
        setError(null);
        setStatus(AsyncStates.LOADING);

        try {
            const { data: options } = await axiosInstance.post<RegisterOpts>(
                process.env.REACT_APP_API_URL + 'v1/data/_passkey_register_begin',
                { user_email },
                { headers }
            );

            const credential = await SWB.startRegistration({ optionsJSON: options });
            const payload = {
                user_email, credential: credential, display_name: values.display_name,
            }

            const { data: { result: { data, status, message } } } = await axiosInstance.post<any>(
                process.env.REACT_APP_API_URL + 'v1/data/_passkey_register_finish',
                payload,
                { headers: headers }
            );

            if (status === 'Success') {
                if (data) {
                    setCredentials((prev) => ({
                        ...prev,
                        data: [data, ...(prev?.data ?? []),]
                    }))
                }
                setStatus(AsyncStates.SUCCESS);
            } else {
                setStatus(AsyncStates.ERROR);
                setError(message);
            }

        } catch (err: any) {
            if (err instanceof SWB?.WebAuthnError && err?.name === 'NotAllowedError') {
                console.log('User canceled authentication');
                return;
            }
            setStatus(AsyncStates.ERROR);
            const errorMessage = err?.response?.data?.detail ?? messages[currentLanguage]?.internal_server_error;
            setError(errorMessage);
        } finally {
            setStatus(AsyncStates.INITIAL)
        }
    }

    const onClose = (id: string, status: string) => {

        setCredentials((prev) => ({
            ...prev,
            data: prev.data?.filter((cred: any) => cred.credential_id !== id)
        }))

        setDeleteModal({
            show: false,
            credential: null,
            loading: false
        })
    }

    if (!SWB?.browserSupportsWebAuthn()) {
        return <p>{t("common.passKeyNotSupported")}</p>;
    }

    return (
        <div
            style={{
                display: "flex",
                gap: "0.5rem",
                flexDirection: "column"
            }}
        >
            <div style={{
                display: "flex",
                gap: "1rem",
                flexDirection: "column"
            }}>
                <div>
                    <h3 >
                        {t("common.addNewCredential")}
                    </h3>
                </div>
                <Form form={form} onFinish={handleRegister} layout="vertical"
                    initialValues={{
                        display_name: getDeviceInfoString()
                    }}
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                        width: "100%",
                        alignItems: "center"
                    }}
                >
                    <Form.Item
                        label={t("common.deviceName")}
                        name="display_name"
                        rules={[{ required: true, message: t("common.pleaseNameThisDevice") }]}
                        style={{
                            width: "80%",
                        }}
                    >
                        <Input placeholder={t("common.enterDeviceName")} />
                    </Form.Item>
                    <Form.Item label={""}
                        style={{
                            margin: "0%",
                        }}>
                        <Button type="primary" htmlType="submit" loading={status === AsyncStates.LOADING}>{t("common.addPasskey")}</Button>
                    </Form.Item>
                </Form>
                <div>
                    {error && <p style={{ color: 'red' }}>{t("common.error")}: {error}</p>}
                </div>
            </div>
            <div>
                <div>
                    <h2>
                        {t("common.yourCredentials")} ({credentials.data.length})
                    </h2>
                </div>

                <List
                    loading={credentials.status === "Loading"}
                    itemLayout="horizontal"
                    dataSource={credentials?.data ?? []}
                    renderItem={(credential: any, index) => (
                        <List.Item
                            actions={[<Button onClick={() => {
                                setDeleteModal((prev) => ({
                                    ...prev,
                                    show: true,
                                    credential,
                                }))
                            }
                            } type='primary' danger icon={<DeleteOutlined />}>{t("common.delete")}</Button>]}
                        >
                            <List.Item.Meta
                                title={<h3 className="credential-name">{credential?.display_name ?? `Credential ${index + 1}`}</h3>}
                            // description={<div>{"Registered on:"} {dayjs.utc(credential.registered_at).format("MMMM Do YYYY, h:mm A") + " (UTC)"}</div>}
                            />
                        </List.Item>
                    )}
                />
                <DeletePasskeyModal setDeleteModal={setDeleteModal} deleteModal={deleteModal} headers={headers} onClose={onClose} />
            </div>
        </div>
    );
}


function DeletePasskeyModal({ setDeleteModal, deleteModal, headers, onClose }: any) {
    const [t] = useTranslate()
    const currentLanguage = useSelector((state: StoreState) => state.language.current);

    const handleOk = async () => {
        setDeleteModal((prev: any) => ({
            ...prev,
            loading: true
        }))

        try {
            const { data: { result: { status, message: apiMessage } } } = await axiosInstance.post<any>(`/v1/data/_passkey_delete`, {
                "credential_id": deleteModal?.credential.credential_id
            }, { headers });

            if (status === 'Success') {
                onClose(deleteModal?.credential.credential_id, status);
                message.success(apiMessage);
            } else {
                message.error(apiMessage);
            }
        } catch (err: any) {
            const errorMessage = err?.response?.data?.detail ?? messages[currentLanguage]?.internal_server_error;
            message.error(errorMessage);
        }
    };

    return (
        <Modal
            title={t("projects.confirmDelete")}
            open={deleteModal.show}
            onOk={handleOk}
            confirmLoading={deleteModal?.loading}
            onCancel={onClose}
            maskClosable={false}
        >
            {t("common.deleteWarningMessage")}?
        </Modal>
    );
}